import { ICapabilities } from 'library/ts/interfaces/utilities/capabilities/ICapabilities';
import { ILoadingAnimationManager } from 'library/ts/managers/loading_animation/interfaces/ILoadingAnimationManager';
import { ILoadingAnimationManagerConfig } from 'library/ts/managers/loading_animation/interfaces/ILoadingAnimationManagerConfig';
import { LoadingAnimationBackgroundImage } from 'library/ts/ui/components/loading/LoadingAnimationBackgroundImage';
import { LoadingAnimationSpinner } from 'library/ts/ui/components/loading/LoadingAnimationSpinner';
import { LoadingAnimationTimeoutMessage } from 'library/ts/ui/components/loading/LoadingAnimationTimeoutMessage';
import { CSSValue } from 'library/ts/ui/enums/css/CSSValue';
import { LiveRegionUtilities } from 'library/ts/utilities/LiveRegionUtilities';
import { Utilities } from 'library/ts/utilities/Utilities';

/**
 * @copyright © Paramount Streaming
 * @author  Stan Gletski
 * @description Tag root level loading animation
 */
export class LoadingAnimationManager implements ILoadingAnimationManager {

	#enabled: boolean = true; // loading animation flag
	#config: ILoadingAnimationManagerConfig;
	#utils: Utilities;
	#capabilities: ICapabilities;
	#visible: boolean = false; // current state of loading animation
	#loadingAnimationContainer: HTMLElement; // root container for loading animation
	#svgAnimationCSSClass: string; // CSS class to add to svg animation
	#fadeInEnabled: boolean;

	#backgroundImageComponent: LoadingAnimationBackgroundImage;
	#spinnerComponent: LoadingAnimationSpinner;
	#timeoutMessageComponent: LoadingAnimationTimeoutMessage;
	#resolvedSpinnerSvg: string; // customSvg from config or default #SVG_SPINNER_ANIMATION; used for getLoadingAnimation() too
	#loadingLiveRegionMessage: string | undefined;
	#playerScopeRoot: HTMLElement | null;

	/**
	 * @description SVG loading animation creative (default when no customSvg)
	 * @type {string}
	 * @private
	 */
	#SVG_SPINNER_ANIMATION: string = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200">' +
		'<g style="fill: rgba( 255, 255, 255, 0.8 ) !important;">' +
		'<path d="M8.16,138.48A99.6,99.6,0,0,0,49,185.78l5.8-14.07a84.94,84.94,0,0,1-.27-143.23l-5.83-14A100.06,100.06,0,0,0,8.16,138.48Z"/>' +
		'<path d="M199.54,85.9A100.13,100.13,0,0,0,76.29,3l5.83,14A85,85,0,0,1,184.38,85.9Z"/>' +
		'<path d="M138.94,192.39a100,100,0,0,0,60.34-76.6H184.07A84.86,84.86,0,0,1,82.43,183.1l-5.8,14.07A99.6,99.6,0,0,0,138.94,192.39Z"/>' +
		'</g>' +
		'</svg>';

	/**
	 * @description Constructor
	 * @param {string} svgAnimationCSSClass
	 * @param {HTMLElement} container
	 * @param {Utilities} utils
	 * @param {ICapabilities} capabilities
	 * @param {boolean} enabled
	 * @param {ILoadingAnimationManagerConfig} config
	 */
	constructor(
		svgAnimationCSSClass: string = undefined,
		container: HTMLElement = undefined,
		utils: Utilities = undefined,
		capabilities: ICapabilities = undefined,
		enabled: boolean = true,
		config: ILoadingAnimationManagerConfig = undefined
	) {
		this.#svgAnimationCSSClass = svgAnimationCSSClass;
		this.#loadingAnimationContainer = container;
		this.#playerScopeRoot = container?.closest( '[smart-tag-parent]' ) as HTMLElement | null;
		this.#utils = utils;
		this.#capabilities = capabilities;
		this.#enabled = enabled;
		this.#config = config;
		this.#resolvedSpinnerSvg = this.#config?.spinner?.customSvg ?? this.#SVG_SPINNER_ANIMATION;
		this.#loadingLiveRegionMessage = this.#config?.accessibility?.loadingMessage;

		// init background image component
		if ( this.#config?.backgroundImage?.enabled && this.#utils.URL.valid( this.#config?.backgroundImage?.imageURL ) ) {
			this.#backgroundImageComponent = new LoadingAnimationBackgroundImage(
				this.#loadingAnimationContainer,
				this.#config?.backgroundImage
			);
		}

		// init timeout message component
		if ( this.#config?.timeoutMessage ) {
			const timeoutConfig = {
				enabled: !!this.#config?.timeoutMessage, // TODO change to (this.#config?.timeoutMessage?.enabled) after ILoadingAnimationTimeoutMessageConfig refactor
				timeoutForMessage: this.#config?.timeoutForMessage,
				timeoutMessage: this.#config?.timeoutMessage
			};
			this.#timeoutMessageComponent = new LoadingAnimationTimeoutMessage(
				this.#loadingAnimationContainer,
				timeoutConfig
			);
		}

		// init spinner component
		if ( this.#config?.spinner?.enabled ) {
			const spinnerSize: string = ( this.#capabilities.isIE ? '9%' : '5%' );
			const spinnerContainer = this.#utils.DOM.stringToElement( `
				<div style="
					position: relative !important;
					width: ${spinnerSize} !important;
					height: ${spinnerSize} !important;
					min-width: 30px !important;
					min-height: 30px !important;
					max-width: 80px !important;
					max-height: 80px !important;
					z-index: 2;"
					aria-hidden="true"
					tabindex="-1">
				</div>
			` );
			this.#loadingAnimationContainer?.appendChild( spinnerContainer );
			this.#spinnerComponent = new LoadingAnimationSpinner( this.#resolvedSpinnerSvg, spinnerContainer, this.#svgAnimationCSSClass );
		}

		this.#fadeInEnabled = this.#config?.fadeIn?.enabled ?? true;
	}

	/**
	 * @description Resolves `#skin-live-region` within this player's Smart Tag root (not document-global).
	 * Skin mounts after Tag init, so query at announce time — not cached at construction.
	 */
	#getLoadingLiveRegionElement = (): HTMLElement | null => {
		return this.#playerScopeRoot?.querySelector( '#skin-live-region' ) ?? null;
	};

	get name(): string {
		return 'LoadingAnimationManager';
	}

	/**
	 * @description Returns loading animation manager root DOM container
	 * @returns {HTMLElement}
	 */
	get container(): HTMLElement {
		return this.#loadingAnimationContainer;
	}

	/**
	 * @description Returns URL of background image
	 * @returns {string}
	 */
	get backgroundImageURL(): string {
		return this.#config?.backgroundImage?.imageURL;
	}

	/**
	 * @description Returns LoadingAnimationSpinner class instance
	 * @param {HTMLElement} container
	 * @param {boolean} useInlineCSS
	 * @return {LoadingAnimationSpinner}
	 */
	getLoadingAnimation = ( container: HTMLElement, useInlineCSS: boolean = true ): LoadingAnimationSpinner => {
		const spinner: LoadingAnimationSpinner = new LoadingAnimationSpinner( this.#resolvedSpinnerSvg, container, this.#svgAnimationCSSClass );
		spinner.show( useInlineCSS );
		return spinner;
	};

	/**
	 * @description Show loading animation
	 * @param {boolean} showSpinner - flag forcing spinner to show. Useful for using overlay as click blocker.
	 * @param {boolean} topLevel - flag forcing loading animation overlay to show at highest layer above all UI
	 */
	show = ( showSpinner: boolean = this.#config?.spinner?.enabled, topLevel: boolean = false ): void => {
		if ( !this.#enabled ) {
			this.hide(); // hide loading animation completely if asked so in constructor
			return;
		}
		if ( this.#visible === true ) {
			return; // already visible
		}

		// show main container
		if ( this.#utils.Type.isElement( this.#loadingAnimationContainer ) && !this.#utils.Type.isEmpty( this.#loadingAnimationContainer?.style ) ) {
			if ( this.#fadeInEnabled ) {
				this.#performFadeIn( topLevel );
			}
			this.#loadingAnimationContainer.style.setProperty( 'opacity', '1', CSSValue.IMPORTANT );
			this.#loadingAnimationContainer.style.setProperty( 'display', 'flex', CSSValue.IMPORTANT );
			this.#loadingAnimationContainer.style.setProperty( 'z-index', topLevel ? '3000' : '100', CSSValue.IMPORTANT );
		}

		// show individual components if enabled
		if ( showSpinner ) {
			this.showSpinner();
		}
		if ( this.#backgroundImageComponent ) {
			this.#backgroundImageComponent.show();
		}
		if ( this.#timeoutMessageComponent ) {
			this.#timeoutMessageComponent.show();
		}

		this.#visible = true;
		if ( this.#loadingLiveRegionMessage ) {
			LiveRegionUtilities.announce( this.#getLoadingLiveRegionElement(), this.#loadingLiveRegionMessage );
		}
	};

	setLoadingLiveRegionMessage = ( message: string ): void => {
		const trimmed = String( message ?? '' ).trim();
		if ( trimmed.length ) {
			this.#loadingLiveRegionMessage = trimmed;
		}
	};

	/**
	 * @description Hide loading animation
	 */
	hide = (): void => {
		if ( !this.#visible ) {
			return; // already hidden
		}

		LiveRegionUtilities.clear( this.#getLoadingLiveRegionElement() );

		// hide individual components
		this.hideSpinner();
		if ( this.#backgroundImageComponent ) {
			this.#backgroundImageComponent.hide();
		}
		if ( this.#timeoutMessageComponent ) {
			this.#timeoutMessageComponent.hide();
		}

		// hide main container
		if ( this.#utils.Type.isElement( this.#loadingAnimationContainer ) && !this.#utils.Type.isEmpty( this.#loadingAnimationContainer?.style ) ) {
			this.#loadingAnimationContainer.style.setProperty( 'display', 'none', CSSValue.IMPORTANT );
		}

		this.#visible = false;
	};

	/**
	 * @description Set background image with provided URL
	 * @param {string} imageURL
	 */
	setBackgroundImage = ( imageURL: string ): void => {
		if ( this.#utils.URL.valid( imageURL ) ) {
			this.#config.backgroundImage.imageURL = imageURL;
			if ( this.#backgroundImageComponent ) {
				this.#backgroundImageComponent.setImage( imageURL );
			} else {
				// Initialize the background image component if it was not already.
				this.#backgroundImageComponent = new LoadingAnimationBackgroundImage(
					this.#loadingAnimationContainer,
					this.#config.backgroundImage
				);
			}
		}
	};

	/**
	 * @description Enable background image
	 */
	enableBackgroundImage = (): void => {
		if ( this.#backgroundImageComponent ) {
			this.#backgroundImageComponent.enable();
		}
	};

	/**
	 * @description Disable and hide background image
	 */
	disableBackgroundImage = (): void => {
		if ( this.#backgroundImageComponent ) {
			this.#backgroundImageComponent.disable();
		}
	};

	/**
	 * @description Enable spinner
	 */
	enableSpinner = (): void => {
		if ( this.#spinnerComponent ) {
			this.#spinnerComponent.enable();
		}
	};

	/**
	 * @description Disable and hide spinner
	 */
	disableSpinner = (): void => {
		if ( this.#spinnerComponent ) {
			this.#spinnerComponent.disable();
		}
	};

	/**
	 * @description Show spinner
	 */
	showSpinner = (): void => {
		if ( this.#spinnerComponent ) {
			this.#spinnerComponent.show();
		}
	};

	/**
	 * @description Hide spinner
	 */
	hideSpinner = (): void => {
		if ( this.#spinnerComponent ) {
			this.#spinnerComponent.hide();
		}
	};

	/**
	 * @description Sets opacity of Spinner background so it can be transparent
	 * @param {number} value - opacity value. if not set - default = 1
	 */
	backgroundOpacity = ( value: number ): void => {
		let bgColor = 'rgba( 0, 0, 0, 1 )';
		if ( isNaN( value ) === false ) bgColor = 'rgba( 0, 0, 0, ' + value + ' )';
		if ( this.#utils.Type.isElement( this.#loadingAnimationContainer ) && !this.#utils.Type.isEmpty( this.#loadingAnimationContainer?.style ) ) {
			this.#loadingAnimationContainer.style.setProperty( 'background-color', bgColor, CSSValue.IMPORTANT );
		}
	};

	/**
	 * @description Enable optimize message
	 */
	enableOptimizeMessage = (): void => {
		if ( this.#timeoutMessageComponent ) {
			this.#timeoutMessageComponent.enable();
		}
	};

	/**
	 * @description Disable and hide optimize message
	 */
	disableOptimizeMessage = (): void => {
		if ( this.#timeoutMessageComponent ) {
			this.#timeoutMessageComponent.disable();
		}
	};

	/**
	 * @description Show optimize message
	 */
	showOptimizeMessage = (): void => {
		if ( this.#timeoutMessageComponent ) {
			this.#timeoutMessageComponent.show();
		}
	};

	/**
	 * @description Hide optimize message
	 */
	hideOptimizeMessage = (): void => {
		if ( this.#timeoutMessageComponent ) {
			this.#timeoutMessageComponent.hide();
		}
	};

	/**
	 * @description Enable fade in animation
	 */
	enableFadeIn = (): void => {
		this.#fadeInEnabled = true;
	};

	/**
	 * @description Disable fade in animation
	 */
	disableFadeIn = (): void => {
		this.#fadeInEnabled = false;
	};

	/**
	 * @description Perform fade in effect
	 */
	#performFadeIn = ( topLevel: boolean ): void => {
		if ( this.#utils.Type.isElement( this.#loadingAnimationContainer ) && !this.#utils.Type.isEmpty( this.#loadingAnimationContainer?.style ) ) {
			this.#loadingAnimationContainer.style.setProperty( 'opacity', '0', CSSValue.IMPORTANT );
			this.#loadingAnimationContainer.style.setProperty( 'transition', this.#getFadeCSS( this.#config?.fadeIn?.duration ), CSSValue.IMPORTANT );
			this.#loadingAnimationContainer.style.setProperty( 'display', 'flex', CSSValue.IMPORTANT );
			this.#loadingAnimationContainer.style.setProperty( 'z-index', topLevel ? '3000' : '100', CSSValue.IMPORTANT );
			void this.#loadingAnimationContainer.offsetHeight; // force reflow
			this.#loadingAnimationContainer.style.setProperty( 'opacity', '1', CSSValue.IMPORTANT ); // trigger fade in
			setTimeout( () => {
				this.#loadingAnimationContainer.style.setProperty( 'transition', 'none', CSSValue.IMPORTANT ); // remove transition
			}, this.#config.fadeIn.duration );
		}
	};

	/**
	 * @description Returns the CSS transition string for the fade-in effect
	 * @param {number} duration
	 * @returns {string}
	 */
	#getFadeCSS = ( duration: number ): string => {
		return `opacity ${duration}ms ease-in-out`;
	};
}